/**
 * Events Listing Page
 * @file src/web/features/events/pages/index.tsx
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardTitle, CardContent, CardDescription } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Alert, AlertTitle, AlertDescription } from '@voilajsx/uikit/alert';
import { useAuth } from '../../auth';
import { useEvents } from '../hooks/useEvents';
import { EventCard } from '../components/EventCard';
import { SEO } from '../../../shared/components';
import {
  Calendar,
  Plus,
  AlertCircle,
  Image,
  Search
} from 'lucide-react';

const EventsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const { events, loading, error, deleteEvent } = useEvents(isAdmin);
  const [searchTerm, setSearchTerm] = useState('');

  const handleEdit = (id: number) => {
    navigate(`/events/admin/edit/${id}`);
  };

  const handleDelete = async (id: number, title: string) => {
    if (window.confirm(`Are you sure you want to delete "${title}"? This will delete all photos and videos in this event.`)) {
      try {
        await deleteEvent(id);
      } catch (err) {
        console.error('Error deleting event:', err);
        alert('Failed to delete event. Please try again.');
      }
    }
  };

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (event.description && event.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (event.location && event.location.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <SEO
        title={isAdmin ? 'Manage Events - Admin Panel' : 'Events & Gallery - Tuition Point'}
        description="View photos and videos from our events and activities"
      />
      <div className="container mx-auto px-4 max-w-7xl py-8">
        <div className="space-y-6">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 rounded-lg p-6 shadow-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                  <Calendar className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight text-white">
                    {isAdmin ? 'Manage Events' : 'Our Events'}
                  </h1>
                  <p className="mt-1 text-cyan-100 text-sm">
                    {isAdmin
                      ? 'Create and manage events with photos and videos'
                      : 'Explore memorable moments from our activities and celebrations'
                    }
                  </p>
                </div>
              </div>

              {isAdmin && (
                <Button
                  onClick={() => navigate('/events/admin/create')}
                  className="bg-white text-cyan-600 hover:bg-cyan-50 shadow-md"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Create Event
                </Button>
              )}
            </div>
          </div>

          {/* Events Section */}
          <Card className="border-t-4 border-t-cyan-600 shadow-md overflow-hidden p-0">
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 px-6 py-5">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 bg-cyan-100 rounded-lg">
                  <Image className="h-5 w-5 text-cyan-600" />
                </div>
                All Events
              </CardTitle>
              <CardDescription className="mt-1.5">
                {isAdmin
                  ? `${events.length} events with photos and videos`
                  : 'Browse through our collection of events'
                }
              </CardDescription>
            </div>

            <CardContent className="p-6">
              {/* Search Bar */}
              <div className="mb-6">
                <div className="relative max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                  />
                </div>
              </div>

              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Events Grid */}
              <div>
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="bg-gray-200 aspect-video rounded-t-xl"></div>
                        <div className="bg-white p-5 rounded-b-xl shadow">
                          <div className="bg-gray-200 h-6 rounded mb-3"></div>
                          <div className="bg-gray-200 h-4 rounded mb-2"></div>
                          <div className="bg-gray-200 h-4 rounded w-2/3"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredEvents.map((event) => (
                      <EventCard
                        key={event.id}
                        event={event}
                        isAdmin={isAdmin}
                        onEdit={isAdmin ? handleEdit : undefined}
                        onDelete={isAdmin ? handleDelete : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>

              {!loading && filteredEvents.length === 0 && (
                <div className="text-center py-12">
                  <Calendar className="mx-auto h-16 w-16 text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">
                    {searchTerm ? 'No events found' : 'No events available'}
                  </h3>
                  <p className="text-gray-500 mb-6">
                    {searchTerm
                      ? 'Try adjusting your search terms'
                      : isAdmin
                        ? 'Start by creating your first event.'
                        : 'Check back soon for new events!'
                    }
                  </p>
                  {isAdmin && !searchTerm && (
                    <Button
                      onClick={() => navigate('/events/admin/create')}
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Create First Event
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default EventsPage;
